// SIL-1424. The customer portal's only caller of
// POST /api/client/purchase-terms/consent (SIL-1364/SIL-1365 shipped the
// route, the DTO and the table; nothing called it until this).
//
// Both call sites — the deposit step on an accepted offer and the auction
// bid dialog — send this immediately before the binding event's own write
// (offer_pay_deposit / place_bid), never at page load: 966's enforcement on
// auction_bid matches on (listing_id, bid_amount_usd, customer_org_id)
// within a 15-minute window, so a consent taken and then left sitting
// expires. Routing both call sites through one function keeps that
// sequencing in one place instead of two copies that can drift out of sync.
import type { PurchaseTermsConsentCreate } from '@siliconx/shared/dto/purchase-terms';
import { api, throwIfNotOk } from './api';

export interface PurchaseTermsConsentResult {
  consentId: string;
}

export async function createPurchaseTermsConsent(
  body: PurchaseTermsConsentCreate,
): Promise<PurchaseTermsConsentResult> {
  const res = await api.api.client['purchase-terms'].consent.$post({ json: body });
  await throwIfNotOk(res);
  return (await res.json()) as unknown as PurchaseTermsConsentResult;
}

/**
 * True when an error thrown by `throwIfNotOk` carries the code the API maps
 * migration 966's PT001 enforcement failure to (`apps/api/src/routes/client/
 * offers.ts` and `listings.ts`, the `place_bid` / `offer_pay_deposit` catch
 * blocks) — i.e. the binding event's own write path rejected it for a
 * missing or expired purchase-terms consent, as distinct from any other
 * reason the same call can fail. Callers use this to route the failure back
 * to the terms step instead of a generic error toast.
 */
export function isPurchaseTermsConsentRequiredError(err: unknown): boolean {
  const body = (err as { body?: { error?: { code?: string } } } | undefined)?.body;
  return body?.error?.code === 'purchase_terms_consent_required';
}
